// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { AlternatePackagingAttributesModel } from "@store/Framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Carousel, { CarouselPresentationProps, CarouselSlide } from "@store/Overrides/Components/Carousel";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import constants from "@store/constants";
import { getAlternatePackagingDataView } from "@store/Framework/Store/Greif/Data/ProductAlternatePackaging/ProductAlternatePackagingSelectors";
import loadProductAlternatePackaging from "@store/Framework/Store/Greif/Data/ProductAlternatePackaging/Handlers/LoadAlternatePackaging";
import AlternativeIcon, { AlternativeIconStyles } from "@store/Library/Components/AlternativeIcon";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";

const enum fields {
    title = "title",
    iconSize = "iconSize",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.iconSize]: number;
    };
}

const AnchorHeading = getWidgetComponent("ProductDetails/ProductDetailsAnchorHeading");

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const productDataView = getProductStateByPath(state, path);
    const productErpNumber = productDataView?.value?.productNumber || "";
    const superAndSubCategories = productErpNumber?.substring(0, 4) || "";
    const productAttributeTypes = productDataView?.value?.attributeTypes || [];

    return {
        path,
        productErpNumber,
        productDataView,
        alternatePackagingPageLink: getPageLinkByPageType(state, constants.pageType.alternatePackagingPage),
        superAndSubCategories,
        productAttributeTypes,
        alternatePackagingDataView: getAlternatePackagingDataView(state, {
            productErpNumber,
            superAndSubCategories,
            attributeTypes: productAttributeTypes,
        }),
    };
};

const mapDispatchToProps = {
    loadProductAlternatePackaging,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasProduct;

interface ProductDetailsAlternativePackagingStyles {
    wrapper?: InjectableCss;
    alternativeTypeWrapper?: InjectableCss;
    carousel?: CarouselPresentationProps;
    slideType?: TypographyPresentationProps;
    alternativeIcon?: AlternativeIconStyles;
    slideSummary?: TypographyPresentationProps;
    clickable?: ClickableProps;
}

const productDetailsAlternativePackagingStyles: ProductDetailsAlternativePackagingStyles = {
    wrapper: {
        css: css`
            ${spacingStyles(pxToRem(32), pxToRem(32))}
        `,
    },
    carousel: {
        arrowsGap: pxToRem(20),
    },
    alternativeIcon: {
        icon: {
            size: 130,
            color: "primary",
            css: css`
                margin: ${pxToRem(20)};
            `,
        },
    },
    alternativeTypeWrapper: {
        css: css`
            position: relative;
            width: 100%;
            height: 100%;
            padding: ${pxToRem(20)};
            border: ${pxToRem(2)} solid ${getColor("common.borderLight")};
        `,
    },
    clickable: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;

            &:hover {
                svg {
                    color: ${getColor("primary.light")};
                    transition: ${transitionHoverStyles("color")};
                }
            }
        `,
    },
    slideType: {
        css: css`
            color: ${getColor("primary")};
            ${({ theme }) => `
                font-size: ${theme.design.typography.size.larger};
                font-weight: ${theme.design.typography.weight.semibold};
            `};
        `,
    },
    slideSummary: {
        css: css`
            text-align: center;
        `,
    },
};

const styles = productDetailsAlternativePackagingStyles;

const getAlternativeShortDescription = (alternative: AlternatePackagingAttributesModel, product: ProductModel) => {
    const filteredCurrentAttributes = product?.attributeTypes?.filter(attribute => {
        if (!alternative) {
            return [];
        }

        return alternative?.alternatePackagingAttributeValueModels!.find(
            x =>
                x.attributeTypeId === attribute.id &&
                x.baseAttributeValueDisplay === attribute.attributeValues![0].valueDisplay,
        );
    });

    const sortedCombinedAttributes = filteredCurrentAttributes
        ?.map(attribute => {
            return {
                attributeId: attribute.id,
                attributeName: attribute.label,
                attributeValue: attribute.attributeValues![0].valueDisplay,
                alternateAttribute: alternative?.alternatePackagingAttributeValueModels!.find(
                    x => x.baseAttributeValueDisplay === attribute.attributeValues![0].valueDisplay,
                )?.alternateAttributeValueDisplay,
                sortOrder: attribute.sortOrder,
            };
        })
        .sort((a, b) =>
            a.sortOrder === b.sortOrder ? a.attributeId.localeCompare(b.attributeId) : a.sortOrder - b.sortOrder,
        );

    const highLevelType = product?.attributeTypes
        ?.find(x => x.name === "PackagingType")
        ?.attributeValues?.map(x => x.valueDisplay);
    const capacity = product?.attributeTypes
        ?.find(x => x.name === "Capacity")
        ?.attributeValues?.map(x => x.valueDisplay);

    return `${highLevelType} ${capacity} / ${sortedCombinedAttributes
        ?.map(attribute => attribute.alternateAttribute)
        .join(" / ")}`;
};

const ProductDetailsAlternativePackaging = ({
    fields: { title, iconSize },
    path,
    productDataView,
    alternatePackagingPageLink,
    productErpNumber,
    superAndSubCategories,
    productAttributeTypes,
    alternatePackagingDataView,
    loadProductAlternatePackaging,
}: Props) => {
    const [productAlternatePackaging, setProductAlternatePackging] = useState<any[]>([]);

    useEffect(() => {
        if (
            !alternatePackagingDataView.value &&
            !alternatePackagingDataView.isLoading &&
            productAlternatePackaging.length === 0
        ) {
            loadProductAlternatePackaging({
                productErpNumber,
                superAndSubCategories,
                attributeTypes: productAttributeTypes,
            });
        }
    }, []);

    useEffect(() => {
        if (alternatePackagingDataView.value && !alternatePackagingDataView.isLoading) {
            const _product = alternatePackagingDataView.value;
            setProductAlternatePackging(_product);
        }
    }, [alternatePackagingDataView]);

    if (!alternatePackagingDataView.value) {
        return null;
    }

    const alternativeIconStyles = mergeProps<ProductDetailsAlternativePackagingStyles["alternativeIcon"]>(
        styles.alternativeIcon,
        {
            icon: {
                size: iconSize,
            },
        },
    );

    const slides: CarouselSlide[] = productAlternatePackaging.map(alternative => ({
        id: alternative.id,
        renderComponent: () => {
            return (
                <StyledWrapper key={alternative.id} {...styles.alternativeTypeWrapper}>
                    <Clickable
                        href={`${alternatePackagingPageLink?.url}?productPath=${path}&type=${alternative.type}`}
                        {...styles.clickable}
                    >
                        <Typography {...styles.slideType}>
                            {`${translate(`${alternative.typeDisplay}`)} ${translate("Choice")}`}
                        </Typography>
                        <AlternativeIcon type={alternative.type} extendedStyles={alternativeIconStyles} />
                        <Typography {...styles.slideSummary}>
                            {productDataView.value &&
                                translate(getAlternativeShortDescription(alternative, productDataView.value) || "")}
                        </Typography>
                    </Clickable>
                </StyledWrapper>
            );
        },
    }));

    const itemsInCarousel = [
        1,
        slides.length > 2 ? 2 : slides.length,
        slides.length > 3 ? 3 : slides.length,
        slides.length > 4 ? 4 : slides.length,
        null,
    ];

    if (!slides.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {title && (
                <AnchorHeading
                    fields={{
                        title,
                        anchor: title,
                    }}
                />
            )}
            <LoadingOverlay loading={alternatePackagingDataView.isLoading || productDataView.isLoading}>
                <Carousel
                    {...styles.carousel}
                    id="alternative-image-gallery"
                    itemsPerSlide={itemsInCarousel}
                    itemsToScroll={itemsInCarousel}
                    showDots
                    slides={slides}
                    useSkipNav={false}
                />
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withProduct(ProductDetailsAlternativePackaging)),
    definition: {
        group: "Product Details",
        displayName: "Product Details Alternate Packaging",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.iconSize,
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 130,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
